import { useRef, useState } from "react";
import { toast } from "sonner";
import { AppShell } from "@/components/layout/AppShell";
import { MenuboardEditor } from "@/components/menuboard/MenuboardEditor";
import { Button, Card, Chip, ListCell, Skeleton, Spinner, TextButton } from "@/components/ui";
import { errorMessage } from "@/lib/api/error";
import {
  useCreateMenuboard,
  useExtractMenuboard,
  useImportMenuboard,
  useMenuboards,
  useSeedMenuboards,
  useUpdateMenuboard,
} from "@/lib/api/queries";
import type { Menuboard, MenuboardDraft } from "@/lib/api/types";
import { useCurrentWorkspace } from "@/stores/workspace";

const EMPTY_DRAFT: MenuboardDraft = { name: "", menus: [] };

export default function MenuboardsPage() {
  const workspaceId = useCurrentWorkspace().workspace?.id ?? "";
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [draft, setDraft] = useState<MenuboardDraft | null>(null);
  const photoInput = useRef<HTMLInputElement>(null);

  const { data: menuboards, isLoading } = useMenuboards(workspaceId);
  const { data: seedMenuboards } = useSeedMenuboards();
  const importMenuboard = useImportMenuboard(workspaceId);
  const updateMenuboard = useUpdateMenuboard(workspaceId);
  const createMenuboard = useCreateMenuboard(workspaceId);
  const extractMenuboard = useExtractMenuboard();

  const importedNames = new Set((menuboards ?? []).map((b) => b.name));
  const availableSeeds = (seedMenuboards ?? []).filter((s) => !importedNames.has(s.name));

  function handlePhoto(file: File | undefined) {
    if (!file) return;
    setDraft(null);
    extractMenuboard.mutate(file, {
      onSuccess: (result) => {
        setExpandedId(null);
        setDraft(result);
        toast.success(`메뉴 ${result.menus.length}개를 읽었어요`);
      },
      onError: (error) =>
        toast.error(errorMessage(error, "사진을 분석하지 못했어요. 직접 입력해 주세요.")),
    });
  }

  function handleCreate(next: MenuboardDraft) {
    createMenuboard.mutate(next, {
      onSuccess: () => {
        setDraft(null);
        toast.success("메뉴판을 만들었어요");
      },
      onError: (error) => toast.error(errorMessage(error, "저장하지 못했어요. 다시 시도해 주세요.")),
    });
  }

  function handleSave(menuboard: Menuboard, next: MenuboardDraft) {
    updateMenuboard.mutate(
      { ...menuboard, ...next },
      {
        onSuccess: () => toast.success("저장했어요"),
        onError: (error) =>
          toast.error(errorMessage(error, "저장하지 못했어요. 다시 시도해 주세요.")),
      },
    );
  }

  function handleImport(seedId: string, seedName: string) {
    importMenuboard.mutate(seedId, {
      onSuccess: () => toast.success(`${seedName} 가져왔어요`),
      onError: () => toast.error("가져오지 못했어요. 다시 시도해 주세요."),
    });
  }

  return (
    <AppShell title="메뉴판">
      <Card variant="filled">
        <h2 className="text-label-1 font-semibold text-label-normal">사진으로 메뉴판 만들기</h2>
        <p className="mt-1 text-caption-1 text-label-alternative">
          메뉴판을 찍으면 메뉴 이름·가격·옵션을 읽어와요. 저장하기 전에 고칠 수 있어요.
        </p>
        <input
          ref={photoInput}
          type="file"
          accept="image/jpeg,image/png,image/webp"
          className="hidden"
          onChange={(e) => {
            handlePhoto(e.target.files?.[0]);
            // 같은 사진을 다시 고를 수 있게 비운다(값이 같으면 change 가 안 뜬다).
            e.target.value = "";
          }}
        />
        {extractMenuboard.isPending ? (
          <div className="mt-4 flex items-center gap-2">
            <Spinner size={20} />
            <span className="text-body-2 text-label-alternative">메뉴판을 읽고 있어요…</span>
          </div>
        ) : (
          <div className="mt-4 flex items-center gap-3">
            <Button iconLeft="camera" onClick={() => photoInput.current?.click()}>
              메뉴판 사진 올리기
            </Button>
            <TextButton onClick={() => setDraft(EMPTY_DRAFT)}>직접 입력하기</TextButton>
          </div>
        )}
      </Card>

      {draft && (
        <div className="mt-4">
          <h2 className="text-label-1 font-semibold text-label-normal">
            {draft.menus.length > 0
              ? `읽어온 메뉴 ${draft.menus.length}개를 확인해 주세요`
              : "메뉴판 직접 입력"}
          </h2>
          <div className="mt-3">
            <MenuboardEditor
              // 새 추출 결과가 오면 편집 중 상태를 버리고 새 초안으로 다시 만든다.
              key={`draft-${draft.menus.map((m) => m.id).join()}-${draft.name}`}
              initial={draft}
              saving={createMenuboard.isPending}
              submitLabel="메뉴판 저장하기"
              onSubmit={handleCreate}
              onCancel={() => setDraft(null)}
            />
          </div>
        </div>
      )}

      <div className="mt-8">
        <h2 className="text-label-1 font-semibold text-label-normal">내 메뉴판</h2>
        {isLoading ? (
          <Skeleton rows={3} className="mt-2" />
        ) : menuboards && menuboards.length > 0 ? (
          <ul className="mt-2">
            {menuboards.map((menuboard) => (
              <li key={menuboard.id}>
                <ListCell
                  title={menuboard.name}
                  description={`메뉴 ${menuboard.menus.length}개`}
                  chevron
                  onClick={() => setExpandedId((id) => (id === menuboard.id ? null : menuboard.id))}
                />
                {expandedId === menuboard.id && (
                  <div className="mt-3 mb-4">
                    <MenuboardEditor
                      initial={{ name: menuboard.name, menus: menuboard.menus }}
                      saving={updateMenuboard.isPending}
                      onSubmit={(next) => handleSave(menuboard, next)}
                    />
                  </div>
                )}
              </li>
            ))}
          </ul>
        ) : (
          <p className="mt-2 text-body-2 text-label-alternative">
            아직 메뉴판이 없어요. 사진을 올리거나 아래에서 가져와 보세요.
          </p>
        )}
      </div>

      {availableSeeds.length > 0 && (
        <div className="mt-8">
          <h2 className="text-label-1 font-semibold text-label-normal">자주 쓰는 메뉴판 가져오기</h2>
          <div className="mt-3 flex flex-wrap gap-2">
            {availableSeeds.map((seed) => (
              <Chip key={seed.id} onClick={() => handleImport(seed.id, seed.name)}>
                {seed.name}
              </Chip>
            ))}
          </div>
        </div>
      )}
    </AppShell>
  );
}
